import React, { useState } from "react";
import {
  useGetA2pAdminRegistrationsQuery,
  useApproveA2pRegistrationMutation,
  useRejectA2pRegistrationMutation,
  useRefreshA2pAdminRegistrationMutation,
  useResyncA2pRegistrationMutation,
  useClearA2pRegistrationSidMutation,
  useApplyA2pRecurringBillingMutation,
} from "@/store/admin_api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import A2pRegistration from "@/model/interface/A2pRegistration";
import { apiErrorMessage } from "@/util/apiError";
import { formatDate } from "@/util/date";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FaCheck, FaExternalLinkAlt, FaTimes } from "react-icons/fa";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { value: "PLATFORM_REVIEW", label: "Pending Review" },
  { value: "PROFILE_PENDING,PROFILE_APPROVED,BRAND_PENDING,BRAND_APPROVED,CAMPAIGN_PENDING", label: "In Progress" },
  { value: "PLATFORM_REJECTED,PROFILE_REJECTED,BRAND_FAILED,CAMPAIGN_FAILED", label: "Rejected" },
  { value: "COMPLETED", label: "Completed" },
  { value: "ALL", label: "All" },
];

const SID_FIELDS = [
  { value: "customerProfileSid", label: "Customer Profile SID" },
  { value: "trustProductSid", label: "Trust Product SID" },
  { value: "brandRegistrationSid", label: "Brand SID" },
  { value: "messagingServiceSid", label: "Messaging Service SID" },
  { value: "campaignSid", label: "Campaign SID" },
];

function registrationTier(registration: A2pRegistration): string {
  if (registration.businessType === "SOLE_PROPRIETOR") return "Sole proprietor";
  if (registration.skipAutomaticSecVet) return "Low volume";
  return "Standard";
}

const STATUS_BADGE_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  PLATFORM_REVIEW: "outline",
  PLATFORM_REJECTED: "destructive",
  PROFILE_REJECTED: "destructive",
  BRAND_FAILED: "destructive",
  CAMPAIGN_FAILED: "destructive",
  COMPLETED: "default",
  CANCELLED: "secondary",
};

function DetailField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="text-sm whitespace-pre-wrap">{value}</p>
    </div>
  );
}

export default function A2pRegistrationsQueue() {
  const [statusTab, setStatusTab] = useState("PLATFORM_REVIEW");
  const { data: registrations, isLoading } = useGetA2pAdminRegistrationsQuery(
    statusTab === "ALL" ? undefined : statusTab,
  );
  const [approveRegistration, { isLoading: isApproving }] = useApproveA2pRegistrationMutation();
  const [rejectRegistration, { isLoading: isRejecting }] = useRejectA2pRegistrationMutation();
  const [refreshRegistration, { isLoading: isRefreshing }] = useRefreshA2pAdminRegistrationMutation();
  const [resyncRegistration, { isLoading: isResyncing }] = useResyncA2pRegistrationMutation();
  const [clearRegistrationSid, { isLoading: isClearingSid }] = useClearA2pRegistrationSidMutation();
  const [applyRecurringBilling, { isLoading: isApplyingRecurring }] = useApplyA2pRecurringBillingMutation();

  const [reviewTarget, setReviewTarget] = useState<A2pRegistration | null>(null);
  const [rejectionNote, setRejectionNote] = useState("");
  const [sidFieldToClear, setSidFieldToClear] = useState("");

  const closeReview = () => {
    setReviewTarget(null);
    setRejectionNote("");
    setSidFieldToClear("");
  };

  const runRepairAction = async (
    action: () => Promise<A2pRegistration>,
    successMessage: string,
    failureMessage: string,
  ) => {
    try {
      const updated = await action();
      setReviewTarget(updated);
      toast.success(successMessage);
    } catch (error) {
      toast.error(apiErrorMessage(error, failureMessage));
    }
  };

  const handleApprove = async () => {
    const organisationId = reviewTarget?.organisation?.id;
    if (!organisationId) return;
    try {
      await approveRegistration(organisationId).unwrap();
      toast.success("Approved and submitted to the carrier");
      closeReview();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to approve the registration"));
    }
  };

  const handleReject = async () => {
    const organisationId = reviewTarget?.organisation?.id;
    if (!organisationId) return;
    if (!rejectionNote.trim()) {
      toast.error("Write a note telling the customer what to fix");
      return;
    }
    try {
      await rejectRegistration({ organisationId, note: rejectionNote.trim() }).unwrap();
      toast.success("Returned to the customer with your note");
      closeReview();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to reject the registration"));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">SMS Registrations</h2>
        <p className="text-muted-foreground text-sm">
          Review registrations before they are submitted to the carrier. Reject with a note to
          return them to the customer for free fixes.
        </p>
      </div>

      <Tabs value={statusTab} onValueChange={setStatusTab}>
        <TabsList>
          {STATUS_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Organisation</TableHead>
            <TableHead>Business</TableHead>
            <TableHead>Website</TableHead>
            <TableHead>Tier</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!registrations?.length && (
            <TableRow>
              <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                No registrations
              </TableCell>
            </TableRow>
          )}
          {registrations?.map((registration) => (
            <TableRow key={registration.id}>
              <TableCell className="font-medium">{registration.organisation?.name || "-"}</TableCell>
              <TableCell>{registration.businessName || "-"}</TableCell>
              <TableCell>
                {registration.websiteUrl ? (
                  <a
                    href={registration.websiteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    {registration.websiteUrl}
                    <FaExternalLinkAlt className="h-3 w-3" />
                  </a>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell>{registrationTier(registration)}</TableCell>
              <TableCell>
                <Badge variant={STATUS_BADGE_VARIANTS[registration.status] || "secondary"}>
                  {registration.status.replace(/_/g, " ")}
                </Badge>
              </TableCell>
              <TableCell>{registration.createdAt ? formatDate(registration.createdAt) : "-"}</TableCell>
              <TableCell>{registration.updatedAt ? formatDate(registration.updatedAt) : "-"}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline" onClick={() => setReviewTarget(registration)}>
                  Review
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={reviewTarget !== null} onOpenChange={(open) => !open && closeReview()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {reviewTarget?.businessName || reviewTarget?.organisation?.name || "Registration"}
            </DialogTitle>
          </DialogHeader>
          {reviewTarget && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <DetailField label="Organisation" value={reviewTarget.organisation?.name} />
                <DetailField label="Legal Name" value={reviewTarget.businessName} />
                <DetailField label="Business Type" value={reviewTarget.businessType} />
                <DetailField label="EIN" value={reviewTarget.ein} />
                <DetailField label="Owner Mobile" value={reviewTarget.proofMobile} />
                <DetailField label="Industry" value={reviewTarget.businessIndustry} />
                <DetailField label="Website" value={reviewTarget.websiteUrl} />
                <DetailField
                  label="Address"
                  value={[reviewTarget.street, reviewTarget.city, reviewTarget.state, reviewTarget.postalCode]
                    .filter(Boolean)
                    .join(", ")}
                />
                <DetailField
                  label="Contact"
                  value={[
                    [reviewTarget.repFirstName, reviewTarget.repLastName].filter(Boolean).join(" "),
                    reviewTarget.repEmail,
                    reviewTarget.repPhone,
                  ]
                    .filter(Boolean)
                    .join(" | ")}
                />
              </div>
              <div className="space-y-3 border-t pt-3">
                <DetailField label="Use Case" value={reviewTarget.useCase} />
                <DetailField label="Campaign Description" value={reviewTarget.campaignDescription} />
                <DetailField label="Message Flow" value={reviewTarget.messageFlow} />
                <DetailField label="Sample 1" value={reviewTarget.messageSample1} />
                <DetailField label="Sample 2" value={reviewTarget.messageSample2} />
                <DetailField label="Opt-In Message" value={reviewTarget.optInMessage} />
                <DetailField label="Opt-Out Message" value={reviewTarget.optOutMessage} />
                <DetailField label="Help Message" value={reviewTarget.helpMessage} />
              </div>
              <div className="space-y-3 border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground">Carrier state</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <DetailField
                    label="Customer Profile"
                    value={
                      reviewTarget.customerProfileSid &&
                      `${reviewTarget.customerProfileSid} (${reviewTarget.customerProfileStatus || "pending"})`
                    }
                  />
                  <DetailField
                    label="Trust Product"
                    value={
                      reviewTarget.trustProductSid &&
                      `${reviewTarget.trustProductSid} (${reviewTarget.trustProductStatus || "pending"})`
                    }
                  />
                  <DetailField
                    label="Brand"
                    value={
                      reviewTarget.brandRegistrationSid &&
                      `${reviewTarget.brandRegistrationSid} (${reviewTarget.brandStatus || "pending"}${
                        reviewTarget.brandScore != null ? `, score ${reviewTarget.brandScore}` : ""
                      })`
                    }
                  />
                  <DetailField label="Messaging Service" value={reviewTarget.messagingServiceSid} />
                  <DetailField
                    label="Campaign"
                    value={
                      reviewTarget.campaignSid &&
                      `${reviewTarget.campaignSid} (${reviewTarget.campaignStatus || "pending"})`
                    }
                  />
                  <DetailField label="Rejection Reason" value={reviewTarget.rejectionReason} />
                </div>
                {reviewTarget.organisation?.id && (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isRefreshing}
                        onClick={() =>
                          runRepairAction(
                            () => refreshRegistration(reviewTarget.organisation!.id!).unwrap(),
                            "Status refreshed from the carrier",
                            "Failed to refresh the status",
                          )
                        }
                      >
                        Refresh status
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isResyncing}
                        onClick={() =>
                          runRepairAction(
                            () => resyncRegistration(reviewTarget.organisation!.id!).unwrap(),
                            "Resynced from the carrier",
                            "Failed to resync from the carrier",
                          )
                        }
                      >
                        Resync from carrier
                      </Button>
                      {reviewTarget.status === "COMPLETED" && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isApplyingRecurring}
                          onClick={() =>
                            runRepairAction(
                              () => applyRecurringBilling(reviewTarget.organisation!.id!).unwrap(),
                              "Recurring billing re-applied",
                              "Failed to re-apply recurring billing",
                            )
                          }
                        >
                          Re-apply recurring billing
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={sidFieldToClear} onValueChange={setSidFieldToClear}>
                        <SelectTrigger className="h-8 w-56 text-xs">
                          <SelectValue placeholder="Stuck SID to clear" />
                        </SelectTrigger>
                        <SelectContent>
                          {SID_FIELDS.map((field) => (
                            <SelectItem key={field.value} value={field.value}>
                              {field.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!sidFieldToClear || isClearingSid}
                        onClick={() =>
                          runRepairAction(
                            () =>
                              clearRegistrationSid({
                                organisationId: reviewTarget.organisation!.id!,
                                field: sidFieldToClear,
                              }).unwrap(),
                            "SID cleared",
                            "Failed to clear the SID",
                          )
                        }
                      >
                        Clear SID
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {reviewTarget.status === "PLATFORM_REVIEW" && (
                <div className="space-y-2 border-t pt-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    Rejection note (sent to the customer)
                  </p>
                  <Textarea
                    rows={3}
                    placeholder="What needs fixing before we can submit this to the carrier..."
                    value={rejectionNote}
                    onChange={(event) => setRejectionNote(event.target.value)}
                  />
                </div>
              )}
            </div>
          )}
          {reviewTarget?.status === "PLATFORM_REVIEW" && (
            <DialogFooter>
              <Button variant="destructive" disabled={isRejecting} onClick={handleReject}>
                {isRejecting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FaTimes className="mr-1 h-3 w-3" />}
                Reject with note
              </Button>
              <Button disabled={isApproving} onClick={handleApprove}>
                {isApproving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FaCheck className="mr-1 h-3 w-3" />}
                Approve and submit
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
